import { Autocomplete, debounce, Stack, TextField } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { Loader, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { OccupancyType } from '@yard/app/api'
import { useYardStores } from '@yard/AppProvider'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { YardSelectorAutocomplete } from './components/YardSelectorAutocomplete'
import { YardSelectorWarning } from './components/YardSelectorWarning'
import { JourneyPosition } from './models/journey-position'

interface Props {
  journeyId: number
  hasError?: boolean
  stackDirection?: 'row' | 'column'
  prefillPosition?: OccupancyType
  skipWarningCheck?: boolean
  onChange: (journeyPosition?: JourneyPosition) => void
  onBlockingIssueChange?: (hasBlockingIssue: boolean) => void
}
export const YardPositionSelector = observer(
  ({
    journeyId,
    hasError,
    stackDirection = 'row',
    prefillPosition,
    skipWarningCheck,
    onChange,
    onBlockingIssueChange,
  }: Props) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const { yardBlockStore, yardPositionSelectorStore } = useYardStores()

    const { loading } = useAsyncFetch(async () => {
      await yardBlockStore.loadYardBlocks()

      if (prefillPosition) {
        await yardPositionSelectorStore.initFromJourney(journeyId, prefillPosition)
        const journeyPosition = yardPositionSelectorStore.getJourneyPosition(journeyId)
        onChange(journeyPosition)

        if (journeyPosition && !skipWarningCheck) {
          await yardPositionSelectorStore.checkPositionWarnings(journeyId, journeyPosition)
          onBlockingIssueChange?.(yardPositionSelectorStore.hasBlockingPositionIssue)
        } else {
          onBlockingIssueChange?.(false)
        }
      }
    }, [yardBlockStore, journeyId])

    useEffect(() => {
      return () => yardPositionSelectorStore.reset()
    }, [yardPositionSelectorStore])

    const debouncedCheckPositionWarnings = skipWarningCheck
      ? undefined
      : useMemo(
          () =>
            debounce(async (position: JourneyPosition) => {
              await yardPositionSelectorStore.checkPositionWarnings(journeyId, position)
              onBlockingIssueChange?.(yardPositionSelectorStore.hasBlockingPositionIssue)
            }, 200),
          [journeyId, onBlockingIssueChange, yardPositionSelectorStore],
        )

    if (loading) return <Loader show />

    const handleChange = () => {
      if (yardPositionSelectorStore.isJourneyPositionCompleted) {
        const journeyPosition = {
          blockId: yardPositionSelectorStore.selectedBlockId!,
          bay: yardPositionSelectorStore.selectedBay!,
          row: yardPositionSelectorStore.selectedRow!,
          tier: yardPositionSelectorStore.selectedTier!,
        }
        yardPositionSelectorStore.setJourneyPosition(journeyId, journeyPosition)
        debouncedCheckPositionWarnings?.(journeyPosition)

        onChange(journeyPosition)
      } else if (!hasError) {
        yardPositionSelectorStore.setPositionWarnings()
        onBlockingIssueChange?.(false)
        onChange()
      }
    }

    return (
      <Stack gap={theme.customSpacing.s} paddingY={theme.customSpacing.xs}>
        <Stack gap={theme.customSpacing.s} flexDirection={stackDirection}>
          <Autocomplete
            options={yardPositionSelectorStore.yardBlocks}
            renderInput={params => (
              <TextField
                {...params}
                label={t('yardBlock', 'Yard block')}
                required
                error={hasError && !yardPositionSelectorStore.selectedBlockId}
                helperText={
                  hasError && !yardPositionSelectorStore.selectedBlockId
                    ? t('fieldIsRequired', 'Field is required')
                    : undefined
                }
                data-cy='allocation-destination-block'
              />
            )}
            getOptionLabel={option => option.name}
            onChange={(_, value) => {
              yardPositionSelectorStore.setSelectedBlockId(value?.id)
              handleChange()
            }}
            fullWidth
            value={
              yardPositionSelectorStore.yardBlocks.find(
                y => y.id === yardPositionSelectorStore.selectedBlockId,
              ) ?? null
            }
          />

          {yardPositionSelectorStore.isSelectedBlockStandard && (
            <>
              <YardSelectorAutocomplete
                label={t('bay', 'Bay')}
                value={yardPositionSelectorStore.selectedBay}
                dataCy='allocation-destination-bay'
                onChange={value => {
                  yardPositionSelectorStore.setSelectedBay(value)
                  handleChange()
                }}
                list={yardPositionSelectorStore.bays}
                hasError={hasError}
              />

              <YardSelectorAutocomplete
                label={t('row', 'Row')}
                value={yardPositionSelectorStore.selectedRow}
                dataCy='allocation-destination-row'
                onChange={value => {
                  yardPositionSelectorStore.setSelectedRow(value)
                  handleChange()
                }}
                list={yardPositionSelectorStore.rows}
                hasError={hasError}
              />

              <YardSelectorAutocomplete
                label={t('tier', 'Tier')}
                value={yardPositionSelectorStore.selectedTier}
                dataCy='allocation-destination-tier'
                onChange={value => {
                  yardPositionSelectorStore.setSelectedTier(value)
                  handleChange()
                }}
                list={yardPositionSelectorStore.tiers}
                hasError={hasError}
              />
            </>
          )}
        </Stack>
        {yardPositionSelectorStore.positionWarnings.length > 0 && (
          <YardSelectorWarning warnings={yardPositionSelectorStore.positionWarnings} />
        )}
      </Stack>
    )
  },
)
